import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ThreeDCanvas from '../components/ThreeDCanvas';
import { COURSES } from '../components/courses/courseData';
import { injectFonts, T } from '../utils/fonts';

/**
 * Home — the 3D scene *is* the page.
 *
 * The copy used to live in a solid 44%-wide panel, which cut the render in half
 * along a hard vertical seam and left the character walled off in the other
 * side. Now the type floats over the scene and a gradient scrim fades the
 * starfield out behind it, so it reads as one composition. The stacked
 * full-width buttons became a row of pills, which is what a hero CTA wants to
 * be — the old shape read as form fields.
 */
const Home = () => {
  const [ctaColor, setCtaColor] = useState(T.gold);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    injectFonts();
    const t = setTimeout(() => setVisible(true), 350);
    return () => clearTimeout(t);
  }, []);

  // Clicking the character dims the CTA, then brings it back.
  const handleColorChange = () => {
    setCtaColor(T.panelUp);
    setTimeout(() => setCtaColor(T.gold), 3700);
  };

  const dimmed = ctaColor !== T.gold;

  return (
    <div className="hero">
      <style>{css}</style>

      <ThreeDCanvas onColorChange={handleColorChange} />
      <div className="hero-scrim" aria-hidden="true" />

      {visible && (
        <div className="hero-copy">
          <span className="hero-badge gg-reveal" style={{ '--d': '0.05s' }}>
            <i aria-hidden="true" />
            ההרשמה פתוחה כעת
          </span>

          <h1 className="hero-title gg-reveal" style={{ '--d': '0.12s' }}>
            <span className="hero-kicker">ברוכים הבאים</span>
            <span className="hero-brand">GoGameDev</span>
          </h1>

          <p className="hero-lead gg-reveal" style={{ '--d': '0.2s' }}>
            הצטרפו אלינו ותלמדו פיתוח משחקים, תכנות ופיתוח עם הדרכות מעשיות
            וחוויות אינטראקטיביות!
          </p>

          <div className="hero-stats gg-reveal" style={{ '--d': '0.28s' }}>
            {[
              [`${COURSES.length}`, 'קורסים'],
              ['10–18', 'גילאים'],
            ].map(([value, label]) => (
              <div className="hero-stat" key={label}>
                <span className="hero-stat-v">{value}</span>
                <span className="hero-stat-l">{label}</span>
              </div>
            ))}
          </div>

          <div className="hero-cta gg-reveal" style={{ '--d': '0.36s' }}>
            <Link
              className="gg-btn"
              to="/courses"
              aria-label="עבור לדף הקורסים שלנו"
              style={dimmed ? { background: ctaColor, color: T.text, boxShadow: 'none' } : undefined}
            >
              הקורסים שלנו ←
            </Link>
            <Link
              className="gg-btn-ghost"
              to="/contact"
              aria-label="שיעור ניסיון חינם — מעבר לדף יצירת קשר"
            >
              שיעור ניסיון חינם
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const css = `
  .hero {
    position: relative;
    width: 100%;
    /* svh, deliberately not dvh: dvh tracks the mobile URL bar as it hides and
       shows during scroll, so the hero — and the WebGL canvas inside it — kept
       resizing while you scrolled toward the footer. svh is the stable small
       value, so the scene holds still. The cost is a sliver of page background
       below the hero once the URL bar retracts. */
    height: 100vh;
    height: 100svh;
    background: ${T.bg};
    font-family: ${T.fontBody};
    overflow: hidden;
    touch-action: pan-y;
  }
  :root[data-theme="light"] .hero {
    background:
      radial-gradient(ellipse at 72% 14%, rgba(255,250,218,.7) 0%, transparent 34%),
      linear-gradient(180deg, #65B9E8 0%, #A9DCF3 54%, #E4F6FD 100%);
  }

  /* Fades the scene out under the copy instead of ending it at a border.
     Nearly opaque at the right edge so the text is always legible, gone by
     the middle so the character keeps its space. */
  .hero-scrim {
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(to left,
      rgba(var(--gg-scrim),0.97) 0%,
      rgba(var(--gg-scrim),0.92) 20%,
      rgba(var(--gg-scrim),0.62) 38%,
      rgba(var(--gg-scrim),0.14) 55%,
      transparent 68%);
  }

  .hero-copy {
    position: absolute;
    top: 0; right: 0; bottom: 0;
    z-index: 3;
    width: min(52%, 660px);
    box-sizing: border-box;
    padding-inline: clamp(24px, 4vw, 76px);
    padding-block: clamp(84px, 12vh, 120px) clamp(36px, 7vh, 72px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    direction: rtl;
    text-align: right;
    /* Let the pointer through to the canvas so the character keeps tracking
       the mouse across the whole screen — only the buttons capture it. */
    pointer-events: none;
  }

  .hero-badge {
    align-self: flex-start;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 14px;
    border-radius: 999px;
    background: var(--gg-godotSoft);
    border: 1px solid var(--gg-line);
    color: ${T.indigoLight};
    font-size: 0.8rem; font-weight: 700;
    margin-bottom: clamp(16px, 2.4vh, 26px);
  }
  .hero-badge i {
    width: 7px; height: 7px; border-radius: 50%;
    background: ${T.green};
    box-shadow: 0 0 8px ${T.green};
    animation: heroPulse 1.9s ease-in-out infinite;
  }
  @keyframes heroPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

  /* Two-line lockup: a quiet Hebrew greeting over the brand. Keeping the Latin
     name on its own line avoids the bidi tangle the old single line had. */
  .hero-title { margin: 0 0 clamp(14px, 2vh, 20px); }
  .hero-kicker {
    display: block;
    font-size: clamp(1rem, 1.6vw, 1.3rem);
    font-weight: 600;
    color: ${T.muted};
    margin-bottom: 4px;
  }
  .hero-brand {
    display: block;
    direction: ltr;
    unicode-bidi: isolate;
    font-family: ${T.fontDisplay};
    font-size: clamp(2.4rem, 5.2vw, 4.1rem);
    line-height: 1.02;
    letter-spacing: -0.02em;
    color: ${T.text};
  }

  .hero-lead {
    font-size: clamp(1rem, 1.25vw, 1.12rem);
    line-height: 1.8;
    color: ${T.muted};
    max-inline-size: 44ch;
    margin: 0 0 clamp(20px, 3vh, 30px);
  }

  /* Solid numbers, not gradient-clipped — the page only needs one thing
     shouting, and that is the CTA. */
  .hero-stats {
    display: flex;
    gap: clamp(20px, 2.4vw, 36px);
    margin-bottom: clamp(22px, 3.4vh, 34px);
  }
  .hero-stat { display: flex; flex-direction: column; }
  .hero-stat-v {
    font-family: ${T.fontDisplay};
    font-size: clamp(1.35rem, 2vw, 1.8rem);
    line-height: 1;
    color: ${T.text};
  }
  .hero-stat-l {
    margin-top: 6px;
    font-size: 0.74rem; font-weight: 700;
    letter-spacing: 0.06em;
    color: ${T.faint};
  }

  .hero-cta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    .hero-copy {
      width: 100%;
      justify-content: flex-end;
      padding-inline: 20px;
      padding-block: 0 calc(env(safe-area-inset-bottom, 0px) + 26px);
    }
    /* Scene on top, copy below — same split as the courses page. */
    .hero-scrim {
      background: linear-gradient(to top,
        rgba(var(--gg-scrim),0.97) 0%,
        rgba(var(--gg-scrim),0.93) 28%,
        rgba(var(--gg-scrim),0.58) 50%,
        rgba(var(--gg-scrim),0.12) 70%,
        transparent 84%);
    }
    .hero-brand { font-size: clamp(2rem, 9vw, 2.8rem); }
    .hero-lead { font-size: 1rem; line-height: 1.7; max-inline-size: none; }
    .hero-stats { gap: 22px; margin-bottom: 20px; }
    /* Tighter side padding so both CTAs still share one row on a phone —
       stacked full-width buttons read as form fields, not calls to action. */
    .hero-cta .gg-btn, .hero-cta .gg-btn-ghost { flex: 1 1 auto; padding-inline: 14px; }
  }

  @media (max-width: 400px) {
    .hero-lead { display: none; }
  }
`;

export default Home;
